import React from "react";
import { Checkbox } from "@material-ui/core";
import styled from "styled-components";
import { MAPPING_TOTAL_HEADERS } from "./table.utils";
import CustomTooltip from "../../../../app/components/tooltip/custom.tooltip";

// Component for the fixed Activities column
const ActivitiesColumn = React.memo(({
  assessments,
  totals,
  activitiesPercentile,
  handleChange,
  handleAllCheck,
  allCheckedStates,
  readOnly = false,
}) => {
  const shortContent = (content) => {
    if (content?.length > 35) {
      return `${content.substring(0, 35)}...`;
    }
    return content;
  };

  return (
    <StickyColumn width={`${activitiesPercentile}%`}>
      {/* NO HEADER - Header is now rendered in parent TableHeader component */}

      {/* Assessment rows */}
      {assessments.map((assessment) => {
        const assessmentKey = assessment.key || assessment.scheduleOfEventsAssessmentId;
        const allChecked = allCheckedStates[assessmentKey] || false;
        const assessmentType = assessment?.assessmentType || "No-Type";
        const displayAssessmentName = assessment?.assessmentName;

        return (
          <DataCell key={assessmentKey}>
            <Checkbox
              style={{ padding: "0px" }}
              checked={allChecked}
              onChange={(e) => {
                handleChange(assessmentKey, "all", e.target.checked);
                handleAllCheck(assessmentKey, e.target.checked);
              }}
              disabled={readOnly}
              name="detailsMonthly"
            />
            <CustomTooltip title={`${assessmentType} - ${displayAssessmentName}`}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1, overflow: "hidden" }}>
                <TypeText>{assessmentType}</TypeText>
                <p style={{ textAlign: "left", margin: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <span>{shortContent(displayAssessmentName)}</span>
                </p>
              </div>
            </CustomTooltip>
          </DataCell>
        );
      })}

      {/* Total rows */}
      {totals.map((_, index) => {
        return (
          <TotalCell key={`total-header-${index}`}>
            {MAPPING_TOTAL_HEADERS[index]}
          </TotalCell>
        );
      })}
    </StickyColumn>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to avoid unnecessary re-renders
  return prevProps.assessments.length === nextProps.assessments.length &&
         prevProps.totals.length === nextProps.totals.length &&
         prevProps.activitiesPercentile === nextProps.activitiesPercentile &&
         JSON.stringify(prevProps.allCheckedStates) === JSON.stringify(nextProps.allCheckedStates);
});

ActivitiesColumn.displayName = 'ActivitiesColumn';

// Component for the Assessment Quantity column
const AssessmentQuantityColumn = ({
  assessments,
  totals,
  getCheckedSum,
}) => {
  return (
    <QuantityColumn>
      {/* NO HEADER - Header is now rendered in parent TableHeader component */}

      {/* Assessment quantity rows */}
      {assessments.map((assessment) => {
        const assessmentKey = assessment.key || assessment.scheduleOfEventsAssessmentId;
        const sum = getCheckedSum ? getCheckedSum(assessmentKey) : 0;
        
        return (
          <CustomTooltip key={assessmentKey} title={sum}>
            <QuantityDataCell key={assessmentKey}>
              {sum}
            </QuantityDataCell>
          </CustomTooltip>
        );
      })}

      {/* Empty total cells */}
      {totals.map((_, index) => (
        <TotalCell key={`quantity-total-${index}`}>
          {/* Empty to maintain alignment */}
        </TotalCell>
      ))}
    </QuantityColumn>
  );
};

AssessmentQuantityColumn.displayName = 'AssessmentQuantityColumn';

// Styled components
const StickyColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: ${(p) => p.width};
  min-width: 520px;
  max-width: 520px;
  position: sticky;
  left: 0;
  z-index: 10;
  background: white;
  box-shadow: 2px 0 4px rgba(0,0,0,0.1);
  flex-shrink: 0;
`;

const QuantityColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 120px;
  min-width: 120px;
  max-width: 120px;
  background: white;
  position: sticky;
  left: 520px;
  z-index: 9;
  flex-shrink: 0;
`;

const BaseCell = styled.div`
  height: 60px;
  min-height: 60px;
  max-height: 60px;
  border-bottom: 1px solid var(--surface-300);
  border-right: 1px solid var(--surface-300);
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
  overflow: hidden;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DataCell = styled(BaseCell)`
  background: var(--White);
  justify-content: flex-start;
  padding: 8px 12px;
  flex-direction: row;
  gap: 8px;
  
  p {
    margin: 0;
    flex: 1;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const QuantityDataCell = styled(BaseCell)`
  background: var(--White);
  justify-content: center;
  padding: 8px 12px;
  font-weight: 500;
  height: 60px;
  min-height: 60px;
  max-height: 60px;
`;

const TotalCell = styled(BaseCell)`
  background: var(--White);
  font-weight: bold;
  height: 50px;
  min-height: 50px;
  max-height: 50px;
  justify-content: flex-end;
  padding: 8px 12px;
  color: var(--text-primary);
  border-top: 1px solid var(--surface-400);
  border-bottom: 1px solid var(--surface-300);
  border-right: 1px solid var(--surface-300);
`;

const TypeText = styled.div`
  min-width: 80px;
  width: auto;
  background-color: var(--surface-50);
  height: fit-content;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 12px;
  font-weight: 500;
  flex-shrink: 0;
`;

export { ActivitiesColumn, AssessmentQuantityColumn };